import React, { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { IconCheckbox } from 'react-icon-checkbox'
import { useDispatch } from 'react-redux'
import { toastState } from '@/utils/Toast.util'
import { request } from '@/utils/axiosUtil'
import Markdown from '@/components/common/Markdown'
import Toast from '@/components/common/Toast'
import { RiFileCopyLine } from 'react-icons/ri'
import InfiniteScroll from 'react-infinite-scroll-component'
import '@/styles/components/speakersView.module.scss'
import 'react-virtualized/styles.css'
import BeatLoader from 'react-spinners/BeatLoader'

const defaultImageUrl =
  'https://images.ctfassets.net/p6iqoqavudao/3lLPw0ct3NWRdllJaozgZr/238aa02d0b3a99038d1969146d7916f1/placeholder.png'

const SpeakersView = ({
  allowBookmark,
  view,
  speakerList = [],
  handleProfileRedirect,
  loadMoreRows,
  reset = false,
  hasMore = true,
  setIsLoading,
}) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const [selectedBookmarks, setSelectedBookmarks] = useState([])
  const [checked] = useState(false)
  const loggedInUserDetails = JSON.parse(localStorage.getItem('userDetails')) || {}
  const [bookmarkedSpeakers, setBookmarkedSpeakers] = useState([])
  const [isToast, setIsToast] = useState(false)
  const [toastContent, setToastContent] = useState(null)
  const [isSpeakersEnabled, setSpeakersEnabled] = useState(false)
  const infiniteLoaderRef = useRef(null)

  useEffect(() => {
    if (reset && infiniteLoaderRef.current) {
      infiniteLoaderRef.current.resetLoadMoreRowsCache(true)
    }
  }, [reset, infiniteLoaderRef])

  const getAllBookMarks = async () => {
    const url = '/bookmark'
    const res = await request({ url: url })
    if (res?.status == 200) {
      const bookmarks = []
      res?.data?.data?.map((item, i) => {
        bookmarks[i] = item?.slug
      })
      setBookmarkedSpeakers(bookmarks)
    } else if (res?.response?.status == 500) {
      router.push(`/${res?.response?.status}`)
    }
  }

  useEffect(() => {
    setIsLoading(true)
    loggedInUserDetails?.access_token && getAllBookMarks()
    loggedInUserDetails?.access_token && setSpeakersEnabled(true)
    setIsLoading(false)
  }, [loggedInUserDetails?.access_token])

  const handleAddBookmark = async (details) => {
    const body = details
    const url = '/bookmark'
    const res = await request({ url: url, method: 'POST', data: JSON.stringify(body) })
    if (res?.data?.success) {
      setToastContent(toastState('ADDBOOKMARK', { type: 'Added' }))
      setIsToast(true)
    } else if (res?.response?.status == 500) {
      router.push(`/${res?.response?.status}`)
    } else if (res?.response?.data?.statusCode == 409) {
      router.push('/409')
    }
  }

  const handleDeleteBookmark = async (details) => {
    const url = '/bookmark'
    const res = await request({ url: url, method: 'DELETE', data: JSON.stringify(details) })
    if (res?.data?.success) {
      getAllBookMarks()
      setToastContent(toastState('REMOVEBOOKMARK', { type: 'Removed' }))
      setIsToast(true)
    } else if (res?.response?.status == 500) {
      router.push(`/${res?.response?.status}`)
    } else if (res?.response?.data?.statusCode == 409) {
      router.push('/409')
    }
  }

  const onBookmarksChange = (name) =>
    setSelectedBookmarks((prev) => {
      const tempPrev = new Set([...prev])
      tempPrev.has(name) ? tempPrev.delete(name) : tempPrev.add(name)
      return [...tempPrev]
    })

  const handleCopyToClipboard = (item) => {
    const type = 'text/html'
    document.getElementById('copyToClipboard').innerHTML = ''
    const copyToClipboardDiv = document.createElement('div')
    copyToClipboardDiv.id = 'copyClipboardData'
    copyToClipboardDiv.style.display = 'block'
    // Create img element for image.
    const img = document.createElement('img')
    img.src = `https:${item?.assets?.fields?.file?.url}?w=295&h=310&q=100&fit=thumb&f=top`
    img.alt = 'Image'
    // Create paragraph element for name.
    const nameP = document.createElement('p')
    const name = document.createElement('a')
    name.href = `${window.location.origin}/speaker/${item.slug}`
    name.innerText = item.name
    name.style.fontFamily = 'AktivGrotesk-Regular'
    name.style.fontSize = '1.2em'
    name.style.display = 'block'
    nameP.appendChild(name)
    // Create paragraph element for subline.
    const subline = document.createElement('p')
    subline.innerHTML = item.subline
    subline.style.fontFamily = 'AktivGrotesk-Regular'
    subline.style.fontSize = '1em'
    subline.style.display = 'block'
    copyToClipboardDiv.appendChild(img)
    copyToClipboardDiv.appendChild(nameP)
    copyToClipboardDiv.appendChild(subline)
    document.getElementById('copyToClipboard').appendChild(copyToClipboardDiv)
    const blob = new Blob([document.getElementById('copyClipboardData')?.innerHTML || ''], { type })
    const data = [new ClipboardItem({ [type]: blob })]
    navigator.clipboard.write(data)
    setToastContent(toastState('COPY', { type: 'Copied' }))
    setIsToast(true)
  }

  return (
    <>
      <div className="hidden" id="copyToClipboard"></div>
      <section className="pb-20 text-white">
        {view == 'Grid' ? (
          <div>
            <InfiniteScroll
              dataLength={speakerList?.length}
              next={loadMoreRows}
              hasMore={hasMore}
              loader={<BeatLoader color="white" size={25} className="m-auto" />}
            >
              <div className="text-white pb-[100px]" name="girdview">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {speakerList?.map((item, i) => (
                    <div className="block h-full bg-black group" key={i}>
                      <div className="relative">
                        <div onClick={() => handleProfileRedirect(item?.slug, i)}>
                          <Link
                            href={`/speaker/${item?.slug}`}
                            key={item?.slug}
                            passHref
                            legacyBehavior
                          >
                            <a>
                              {item?.assets?.fields?.file?.url !== undefined ? (
                                <Image
                                  src={`https:${item?.assets?.fields?.file?.url}?w=290&h=365&q=100&fit=thumb&f=top`}
                                  alt=""
                                  width={290}
                                  height={365}
                                  quality={100}
                                  className="cursor-pointer"
                                />
                              ) : (
                                <Image
                                  src={`${defaultImageUrl}?w=290&h=365&q=100&fit=thumb&f=top`}
                                  alt=""
                                  width={290}
                                  height={365}
                                  quality={100}
                                  className="cursor-pointer"
                                />
                              )}
                            </a>
                          </Link>
                        </div>

                        {loggedInUserDetails?.userDetails?.isInternalUser && (
                          <button
                            className="absolute right-12 top-4 text-custom-gray-80 hover:text-white"
                            onClick={() => handleCopyToClipboard(item, i)}
                          >
                            <RiFileCopyLine role="tooltip" className="w-6 h-6 text-[#FF1010]" />
                          </button>
                        )}

                        {isSpeakersEnabled &&
                          allowBookmark &&
                          (loggedInUserDetails ? (
                            <>
                              {bookmarkedSpeakers?.includes(item?.slug) ? (
                                <button
                                  className="absolute w-6 h-6 right-4 top-4"
                                  onClick={() => {
                                    bookmarkedSpeakers?.includes(item?.slug) &&
                                      bookmarkedSpeakers?.splice(
                                        bookmarkedSpeakers?.indexOf(item?.slug),
                                        1
                                      )
                                    handleDeleteBookmark({
                                      bookmark: item?.id,
                                    })
                                  }}
                                >
                                  <Image
                                    src="/static/icons/bookmarked.svg"
                                    height={24}
                                    width={20}
                                    alt="Bookmarked"
                                  />
                                </button>
                              ) : (
                                <button
                                  className="h-6 w-6 stroke-[#c1c1c1] fill-[#404040] right-4 top-4 absolute"
                                  onClick={() => {
                                    !bookmarkedSpeakers?.includes(item?.slug) &&
                                      bookmarkedSpeakers.push(item?.slug)
                                    handleAddBookmark({
                                      bookmark: item?.id,
                                    })
                                  }}
                                >
                                  <Image
                                    src="/static/icons/bookmark.svg"
                                    height={24}
                                    width={20}
                                    alt="Bookmark"
                                  />
                                </button>
                              )}
                            </>
                          ) : (
                            <IconCheckbox
                              checked={
                                selectedBookmarks.indexOf(item?.name) === -1 ? checked : !checked
                              }
                              uncheckedIcon={
                                <button className="absolute w-6 h-6 fill-slate-300 opacity-30 right-2 top-2">
                                  <Image
                                    src="/static/icons/bookmarked.svg"
                                    height={26}
                                    width={21}
                                    alt="Bookmarked"
                                  />
                                </button>
                              }
                              onClick={() => onBookmarksChange(item?.name)}
                            />
                          ))}
                      </div>
                      <div className="speaker-white-text pb-5">
                        <div className="pt-2 mr-3 text-sm font-medium cursor-pointer xs:text-base xs:mt3 regular-font group-hover:link-underline">
                          <p
                            className="cursor-pointer"
                            onClick={() => handleProfileRedirect(item.slug, i)}
                          >
                            {item.name}
                          </p>
                        </div>
                        <div className="w-[95%] text-xs font-normal text-left xs:text-sm text-custom-gray-90 regular-font group-hover:text-white">
                          <p
                            className="cursor-pointer"
                            onClick={() => handleProfileRedirect(item.slug, i)}
                          >
                            {item?.subline && <Markdown data={item.subline} />}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </InfiniteScroll>
          </div>
        ) : (
          <div>
            <InfiniteScroll
              dataLength={speakerList?.length}
              next={loadMoreRows}
              hasMore={hasMore}
              loader={<BeatLoader color="#fff" size={25} />}
            >
              <div className="text-white pt-4 pb-[100px]" id="related">
                {speakerList?.map((item, i) => (
                  <div
                    key={i}
                    className={classNames(
                      'flex mb-2 items-center bg-black shadow-md md:flex-row flex-row border-[1.5px] border-gray-500 group'
                    )}
                  >
                    <div className="w-4/12 xs:w-20">
                      <div
                        className="relative w-auto"
                        onClick={() => handleProfileRedirect(item.slug, i)}
                      >
                        <Link
                          href={`/speaker/${item?.slug}`}
                          key={item?.slug}
                          passHref={true}
                          legacyBehavior
                        >
                          <a>
                            {item?.assets?.fields?.file?.url !== undefined ? (
                              <div
                                role="presentation"
                                className={classNames('cursor-pointer speaker-profile-img')}
                                style={{
                                  backgroundImage: `url(${
                                    'https:' + item?.assets?.fields?.file?.url
                                  }?w=200&q=100&h=200)`,
                                  backgroundPosition: 'top',
                                  backgroundSize: 'cover',
                                }}
                              ></div>
                            ) : (
                              <div
                                role="presentation"
                                className="cursor-pointer speaker-profile-img"
                                style={{
                                  backgroundImage: `url(${'https://images.ctfassets.net/p6iqoqavudao/3lLPw0ct3NWRdllJaozgZr/238aa02d0b3a99038d1969146d7916f1/placeholder.png'}?w=200&q=100&h=200)`,
                                  backgroundPosition: 'top',
                                  backgroundSize: 'cover',
                                }}
                              ></div>
                            )}
                          </a>
                        </Link>
                      </div>
                    </div>
                    <div className="grid w-full leading-normal">
                      <div className="flex-wrap items-center md:flex maxSm:py-1 maxSm:pb-2">
                        <div className="ml-3 text-sm font-medium cursor-pointer xs:text-base regular-font group-hover:link-underline">
                          <p
                            className="cursor-pointer"
                            onClick={() => handleProfileRedirect(item.slug, i)}
                          >
                            {item?.name && <Markdown data={item.name} />}
                          </p>
                        </div>
                        <div className="maxSm:ml-2 ml-3 md:ml-2 maxSm:px-1 text-xs font-normal text-left cursor-pointer xs:text-sm text-custom-gray-90 regular-font md:px-1 group-hover:text-white">
                          <p
                            className="cursor-pointer speakerBioEllipsis"
                            role="presentation"
                            onClick={() => handleProfileRedirect(item.slug, i)}
                          >
                            {item?.subline && <Markdown data={item.subline} />}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center col-end-7 gap-3 place-items-baseline">
                      {loggedInUserDetails?.userDetails?.isInternalUser && (
                        <button
                          className="relative w-6 h-6 mb-2 ml-2 top-1"
                          onClick={() => handleCopyToClipboard(item, i)}
                        >
                          <RiFileCopyLine role="tooltip" className="w-6 h-6 text-[#FF1010]" />
                        </button>
                      )}

                      {isSpeakersEnabled &&
                        (loggedInUserDetails ? (
                          <>
                            {bookmarkedSpeakers?.includes(item?.slug) ? (
                              <button
                                className="relative top-0 right-0 w-6 h-6 ml-2 mr-5 maxSm:mr-3"
                                onClick={() => {
                                  bookmarkedSpeakers?.includes(item?.slug) &&
                                    bookmarkedSpeakers?.splice(
                                      bookmarkedSpeakers?.indexOf(item?.slug),
                                      1
                                    )
                                  handleDeleteBookmark({
                                    bookmark: item?.id,
                                  })
                                }}
                              >
                                <Image
                                  src="/static/icons/bookmarked.svg"
                                  height={24}
                                  width={20}
                                  alt="Bookmarked"
                                />
                              </button>
                            ) : (
                              <button
                                className="h-6 w-6 stroke-[#c1c1c1] fill-[#404040] right-0 relative ml-2 top-0 mr-5 maxSm:mr-3"
                                onClick={() => {
                                  !bookmarkedSpeakers?.includes(item?.slug) &&
                                    bookmarkedSpeakers.push(item?.slug)
                                  handleAddBookmark({
                                    bookmark: item?.id,
                                  })
                                }}
                              >
                                <Image
                                  src="/static/icons/bookmark.svg"
                                  height={24}
                                  width={20}
                                  alt="Bookmark"
                                />
                              </button>
                            )}
                          </>
                        ) : (
                          <IconCheckbox
                            checked={
                              selectedBookmarks.indexOf(item?.name) === -1 ? checked : !checked
                            }
                            uncheckedIcon={
                              <button className="relative w-6 h-6 ml-2 fill-slate-300 opacity-30 right-2 bottom-2">
                                <Image
                                  src="/static/icons/bookmarked.svg"
                                  height={26}
                                  width={21}
                                  alt="Bookmarked"
                                />
                              </button>
                            }
                            onClick={() => onBookmarksChange(item?.name)}
                          />
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </InfiniteScroll>
          </div>
        )}
      </section>
      {isToast && (
        <Toast
          variant={toastContent?.variant}
          message={toastContent?.message}
          handleClick={() => setIsToast(false)}
        />
      )}
    </>
  )
}

export default React.memo(SpeakersView)
